import {defineStore} from 'pinia';
import {ref,watch,onScopeDispose} from 'vue';
import {useWalletStore} from './wallet';
import {useAppChromeStore} from './appChrome';
import {request} from '@/common/services/request';

export const useProfileStore = defineStore('profile', () => {
  const wallet = useWalletStore();
  const nickname = ref<string|null>(null);
  let generation = 0;
  let saving = false;
  let inFlight: Promise<void> | null = null;
  let lastRefreshAt = 0;
  const PROFILE_TTL_MS = 15_000;
  async function refresh(force = false) {
    if (saving) return;
    if (!force) {
      if (inFlight) return inFlight;
      if (lastRefreshAt && Date.now() - lastRefreshAt < PROFILE_TTL_MS) return;
    }
    const version = ++generation;
    const address = wallet.address;
    if (!address || !wallet.isAuthenticated) return;
    inFlight = (async () => {
      try {
        const data = await request<{nickname:string|null}>({url:'/v1/profile',method:'GET',expectedWallet:address,showError:false});
        if (generation === version) {
          nickname.value = data.nickname;
          lastRefreshAt = Date.now();
        }
      } catch { /* Keep the address fallback when profile loading fails. */ }
      finally { if (inFlight) inFlight = null; }
    })();
    return inFlight;
  }
  watch(() => [wallet.address, wallet.isAuthenticated], () => {
    ++generation;
    nickname.value = null;
    lastRefreshAt = 0;
    void refresh(true);
  }, {immediate:true,flush:'sync'});
  if (typeof window !== 'undefined') {
    // 合约/AI 等二级页不停刷 profile；主 Tab 再拉
    const visibleRefresh = () => {
      if (document.hidden || !useAppChromeStore().isMainTabbarPath()) return;
      void refresh(false);
    };
    const timer = window.setInterval(visibleRefresh, 60_000);
    window.addEventListener('focus', visibleRefresh);
    document.addEventListener('visibilitychange', visibleRefresh);
    onScopeDispose(() => {
      window.clearInterval(timer);
      window.removeEventListener('focus', visibleRefresh);
      document.removeEventListener('visibilitychange', visibleRefresh);
    });
  }
  async function save(value:string) {
    const version = ++generation;
    const address = wallet.address;
    if (!address || !wallet.isAuthenticated) throw new Error('Authentication required.');
    saving = true;
    try {
      const data = await request<{nickname:string}>({url:'/v1/profile',method:'PATCH',data:{nickname:value},expectedWallet:address,showError:false});
      if (version !== generation) throw new Error('Wallet changed.');
      nickname.value = data.nickname;
    } finally { saving = false; if (version !== generation) void refresh(); }
  }
  return {nickname,save,refresh};
});
